import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import axiosInstance from '../axios';
import useDebounce from '../hooks/useDebounce';
import '../styles/property-list.css';
import { getFrontendBaseUrl } from '../utils/propertyUrl';
import PropertyCard from './property/PropertyCard';
import PropertyCardSkeleton from './property/PropertyCardSkeleton';
import PropertyEmptyState from './property/PropertyEmptyState';
import PropertyFilters from './property/PropertyFilters';

const LIST_ENDPOINT = '/listing-properties';
const PAGE_SIZE = 12;

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

const PropertyList = ({ showAllLink = false }) => {
    const [searchParams] = useSearchParams();
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [sort, setSort] = useState('latest');
    const [propertyType, setPropertyType] = useState('');
    const [city, setCity] = useState('');
    const [province, setProvince] = useState('');
    const [district, setDistrict] = useState('');
    const [bedrooms, setBedrooms] = useState('');
    const [bathrooms, setBathrooms] = useState('');
    const [furnishing, setFurnishing] = useState('');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');

    const sentinelRef = useRef(null);
    const abortRef = useRef(null);

    const urlCategory = searchParams.get('category') || '';

    const debouncedSearch = useDebounce(search, 400);

    const currentFilters = useMemo(
        () => ({
            page,
            search: debouncedSearch,
            status,
            sort,
            propertyType: propertyType || urlCategory,
            city,
            province,
            district,
            bedrooms,
            bathrooms,
            furnishing,
            priceMin,
            priceMax,
        }),
        [
            page,
            debouncedSearch,
            status,
            sort,
            propertyType,
            urlCategory,
            city,
            province,
            district,
            bedrooms,
            bathrooms,
            furnishing,
            priceMin,
            priceMax,
        ]
    );

    const fetchProperties = useCallback(
        async ({ pageNum = 1, append = false } = {}) => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                setError('');

                if (append) {
                    setLoadingMore(true);
                } else {
                    setLoading(true);
                }

                const params = {
                    page: pageNum,
                    per_page: PAGE_SIZE,
                    sort: currentFilters.sort,
                };

                if (currentFilters.search.trim()) params.search = currentFilters.search.trim();
                if (currentFilters.status !== 'all') params.status = currentFilters.status;
                if (currentFilters.propertyType) params.property_type = currentFilters.propertyType;
                if (currentFilters.city) params.city = currentFilters.city;
                if (currentFilters.province) params.province = currentFilters.province;
                if (currentFilters.district) params.district = currentFilters.district;
                if (currentFilters.bedrooms) params.bedrooms = currentFilters.bedrooms;
                if (currentFilters.bathrooms) params.bathrooms = currentFilters.bathrooms;
                if (currentFilters.furnishing) params.furnishing_status = currentFilters.furnishing;
                if (currentFilters.priceMin) params.price_min = currentFilters.priceMin;
                if (currentFilters.priceMax) params.price_max = currentFilters.priceMax;

                const response = await axiosInstance.get(LIST_ENDPOINT, {
                    params,
                    signal: controller.signal,
                    headers: { 'X-Platform': 'Web' },
                });

                const payload = response.data || {};
                const items = Array.isArray(payload.data) ? payload.data : [];
                const meta = payload.meta || {};

                setAllItems((prev) => {
                    if (!append) return items;
                    const merged = [...prev];
                    items.forEach((item) => {
                        if (!merged.some((existing) => existing.id === item.id)) {
                            merged.push(item);
                        }
                    });
                    return merged;
                });

                setPage(meta.current_page || pageNum);
                setTotal(meta.total || 0);
                setHasMore(Boolean(meta.has_more ?? meta.current_page < meta.last_page));
            } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError') return;
                console.error('Error fetching properties:', err);
                setError('Gagal memuat properti. Silakan coba lagi.');
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [currentFilters]
    );

    useEffect(() => {
        setAllItems([]);
        setPage(1);
        setHasMore(true);
        setTotal(0);
        fetchProperties({ pageNum: 1, append: false });
    }, [
        debouncedSearch,
        status,
        sort,
        propertyType,
        urlCategory,
        city,
        province,
        district,
        bedrooms,
        bathrooms,
        furnishing,
        priceMin,
        priceMax,
    ]);

    useEffect(() => {
        if (!hasMore || loadingMore || loading) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    fetchProperties({ pageNum: page + 1, append: true });
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, page, fetchProperties]);

    useEffect(() => {
        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, []);

    const filterOptions = useMemo(() => {
        return {
            propertyTypes: uniq(allItems.map((p) => p.property_type)),
            cities: uniq(allItems.map((p) => p.city)),
            provinces: uniq(allItems.map((p) => p.province)),
            districts: uniq(allItems.map((p) => p.district)),
        };
    }, [allItems]);

    const isFiltered =
        Boolean(debouncedSearch.trim()) ||
        status !== 'all' ||
        sort !== 'latest' ||
        Boolean(urlCategory) ||
        Boolean(propertyType) ||
        Boolean(city) ||
        Boolean(province) ||
        Boolean(district) ||
        Boolean(bedrooms) ||
        Boolean(bathrooms) ||
        Boolean(furnishing) ||
        Boolean(priceMin) ||
        Boolean(priceMax);

    const canonicalUrl = urlCategory
        ? `${getFrontendBaseUrl()}/properties?category=${urlCategory}`
        : `${getFrontendBaseUrl()}/properties`;

    const handleClearFilters = () => {
        setSearch('');
        setStatus('all');
        setSort('latest');
        setPropertyType('');
        setCity('');
        setProvince('');
        setDistrict('');
        setBedrooms('');
        setBathrooms('');
        setFurnishing('');
        setPriceMin('');
        setPriceMax('');
    };

    return (
        <main className="property-page">
            <Helmet>
                <title>Properti Dijual & Disewa | Cari Rumah Impian</title>
                <meta
                    name="description"
                    content="Cari rumah, tanah, ruko, dan properti terbaik dengan pencarian cepat, modern, dan SEO-friendly."
                />
                <meta name="robots" content={isFiltered ? 'noindex,follow' : 'index,follow'} />
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            <section className="property-hero">
                <div className="container">
                    <div className="property-hero-content">
                        <div>
                            <p className="hero-kicker">
                                {urlCategory
                                    ? `Kategori: ${urlCategory.charAt(0).toUpperCase() + urlCategory.slice(1)}`
                                    : 'Temukan Properti Terbaik'}
                            </p>
                            <h1 className="hero-title">
                                {urlCategory
                                    ? `Properti ${urlCategory.charAt(0).toUpperCase() + urlCategory.slice(1)}`
                                    : 'Listing Properti lengkap, dan SEO Friendly'}
                            </h1>
                        </div>
                        {showAllLink && (
                            <Link
                                to="/all-properties"
                                className="btn-outline-mp text-white"
                                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            >
                                Lihat Semua Properti
                            </Link>
                        )}
                    </div>

                    <PropertyFilters
                        search={search}
                        onSearchChange={setSearch}
                        status={status}
                        onStatusChange={setStatus}
                        sort={sort}
                        onSortChange={setSort}
                        propertyType={propertyType}
                        onPropertyTypeChange={setPropertyType}
                        city={city}
                        onCityChange={setCity}
                        province={province}
                        onProvinceChange={setProvince}
                        district={district}
                        onDistrictChange={setDistrict}
                        propertyTypes={filterOptions.propertyTypes}
                        cities={filterOptions.cities}
                        provinces={filterOptions.provinces}
                        districts={filterOptions.districts}
                        bedrooms={bedrooms}
                        onBedroomsChange={setBedrooms}
                        bathrooms={bathrooms}
                        onBathroomsChange={setBathrooms}
                        furnishing={furnishing}
                        onFurnishingChange={setFurnishing}
                        priceMin={priceMin}
                        onPriceMinChange={setPriceMin}
                        priceMax={priceMax}
                        onPriceMaxChange={setPriceMax}
                        onClear={handleClearFilters}
                    />
                </div>
            </section>

            <section className="property-content">
                <div className="container">
                    <div className="property-summary">
                        <span>{total} properti ditemukan</span>
                        {loadingMore && <span className="muted">Memuat data tambahan...</span>}
                    </div>

                    {error ? (
                        <div className="property-error-state">{error}</div>
                    ) : loading ? (
                        <div className="property-grid">
                            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                                <PropertyCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : allItems.length === 0 ? (
                        <PropertyEmptyState />
                    ) : (
                        <>
                            <div className="property-grid">
                                {allItems.map((property) => (
                                    <PropertyCard key={property.id} property={property} />
                                ))}
                                {loadingMore &&
                                    Array.from({ length: PAGE_SIZE }).map((_, i) => (
                                        <PropertyCardSkeleton key={`skeleton-${i}`} />
                                    ))}
                            </div>

                            {!hasMore && allItems.length > 0 && (
                                <div className="property-pagination">
                                    <div className="property-pagination__info">Semua properti telah ditampilkan</div>
                                </div>
                            )}

                            <div ref={sentinelRef} className="infinite-scroll-sentinel" />
                        </>
                    )}
                </div>
            </section>
        </main>
    );
};

export default PropertyList;
